import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore, type SetStateAction } from 'react';

export interface ExplorerSort { key: 'name' | 'size' | 'modified'; ascending: boolean }
const defaults: ExplorerSort = {key:'name',ascending:true};
const eventName='skrepka-explorer-sort-change';
const subscribe=(listener:()=>void)=>{
  const storage=(event:StorageEvent)=>{if(event.key)memory.delete(event.key);else memory.clear();listener();};
  window.addEventListener('storage',storage);window.addEventListener(eventName,listener);
  return ()=>{window.removeEventListener('storage',storage);window.removeEventListener(eventName,listener);};
};
// Memory fallback also preserves the choice between folders when storage is disabled.
const memory=new Map<string,string>();
function read(key:string){if(memory.has(key))return memory.get(key)!;try{return localStorage.getItem(key)||'';}catch{return '';}}
export function parseExplorerSort(value:string):ExplorerSort {
  try{const parsed=JSON.parse(value) as Partial<ExplorerSort>;if(['name','size','modified'].includes(parsed.key||'')&&typeof parsed.ascending==='boolean')return parsed as ExplorerSort;}catch{/* Use the established default. */}
  return defaults;
}
export function useExplorerSort():[ExplorerSort,(action:SetStateAction<ExplorerSort>)=>void]{
  const client=useQueryClient();
  const userId=useSyncExternalStore(useCallback((listener:()=>void)=>client.getQueryCache().subscribe(listener),[client]),useCallback(()=>client.getQueryData<{id:number}>(['current-user'])?.id??'guest',[client]),()=> 'guest' as const);
  const key=`skrepka-explorer-sort:${userId}`;
  const snapshot=useSyncExternalStore(subscribe,useCallback(()=>read(key),[key]),()=> '');
  const update=useCallback((action:SetStateAction<ExplorerSort>)=>{
    const next=typeof action==='function'?action(parseExplorerSort(read(key))):action;
    const value=JSON.stringify(next);memory.set(key,value);
    try{localStorage.setItem(key,value);}catch{/* Keep the selection for this session. */}
    window.dispatchEvent(new Event(eventName));
  },[key]);
  return [parseExplorerSort(snapshot),update];
}
